import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { useRetryOnError } from '@euler/app/flows/task/functions/useRetryOnError';
import { ErrorFallback } from '@euler/components/error';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ErrorBoundary } from '@sentry/react';
import { ComponentType, FC, memo, ReactNode, Suspense } from 'react';
import { ScrollView, StyleProp, ViewStyle } from 'react-native';

export const SuspenseContentWrapper: FC<{
  resourceId?: string;
  resourceType?: 'observable' | 'async';
  fallback?: NonNullable<ReactNode> | null;
  Loader?: ComponentType<any>;
  ErrorBoundaryComponent?:
    | (typeof ErrorBoundary & { children?: ReactNode })
    | null;
  children?: ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  resetError?: () => void;
}> = memo(props => {
  const { rpx } = useResponsiveContext();
  const { errorBoundaryKey, retryOnError } = useRetryOnError(
    props.resourceId ?? '',
    props.resourceType ?? 'observable',
  );
  const Loader = props.Loader ?? DefaultLoader;
  const ErrorBoundaryComponent = props.ErrorBoundaryComponent ?? ErrorBoundary;

  const contents = (
    <Suspense
      fallback={
        props.fallback ?? (
          <ScrollView
            contentContainerStyle={[
              {
                flexGrow: 1,
                paddingTop: rpx(44),
                justifyContent: 'flex-start',
                alignItems: 'center',
              },
              props.contentContainerStyle,
            ]}
          >
            <Loader />
          </ScrollView>
        )
      }
    >
      {props.children}
    </Suspense>
  );

  if (props.ErrorBoundaryComponent === null) {
    return contents;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <ErrorBoundaryComponent
      key={errorBoundaryKey}
      // @ts-ignore
      fallback={({ error, eventId, componentStack, resetError }) => (
        <ErrorFallback
          error={error}
          eventId={eventId}
          componentStack={componentStack ?? undefined}
          resetError={() => {
            retryOnError();
            resetError();
            props.resetError?.();
          }}
        />
      )}
    >
      {contents}
    </ErrorBoundaryComponent>
  );
});
